import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { categories } from "../advanced-flow/triggers";
import {
  messageCostSchema,
  OutreachType,
} from "../customer-event/events/outreach";
import { MarketingChannel } from "../marketing";
export type {
  CampaignRecipientMetricEvent,
  MessageCost,
} from "../customer-event/events/outreach";

export enum CampaignRecipientStatus {
  PENDING = "pending",
  PROCESSING = "processing",
  PROCESSED = "processed",
  FAILED = "failed",
}

export const terminalStatuses = [
  CampaignRecipientStatus.FAILED,
  CampaignRecipientStatus.PROCESSED,
];

const campaignRecipientMetricsSchema = z.object({
  sends: z.number(),
  deliveries: z.number(),
  opens: z.number(),
  clicks: z.number(),
  bounces: z.number(),
  complaints: z.number(),
  upsellRevenue: z.number(),
  upsellCount: z.number(),
  alreadyUnsubscribes: z.number(),
  invalidChannelIds: z.number(),
  suppressions: z.number(),
  smartSendingSkips: z.number(),
  unsupportedInternationals: z.number(),
  failures: z.number(),
  notEnabled: z.number(),
  quietHours: z.number(),
});

export const smsMessageContentSchema = z.object({
  type: z.literal(OutreachType.SMS),
  text: z.string(),
  generatedContentId: zExt.objectId().nullish(),
});
export const mmsMessageContentSchema = z.object({
  type: z.literal(OutreachType.MMS),
  text: z.string().optional(),
  media: z.array(z.string()),
  generatedContentId: zExt.objectId().nullish(),
});
export const textMessageContentSchema = z.union([
  smsMessageContentSchema,
  mmsMessageContentSchema,
]);
export const emailMessageContentSchema = z.object({
  url: z.string().nullish(),
});
export const messageContentSchema = z.union([
  textMessageContentSchema,
  emailMessageContentSchema,
]);

export type SmsMessageContent = z.infer<typeof smsMessageContentSchema>;
export type MmsMessageContent = z.infer<typeof mmsMessageContentSchema>;
export type TextMessageContent = z.infer<typeof textMessageContentSchema>;
export type EmailMessageContent = z.infer<typeof emailMessageContentSchema>;
export type MessageContent = z.infer<typeof messageContentSchema>;

export const campaignRecipientSchema = z.object({
  _id: zExt.objectId(),
  teamId: zExt.objectId(),
  campaignId: zExt.objectId().nullish(),
  templateId: z.coerce.string().nullish(),
  automationId: zExt.objectId().nullish(),
  automationStepId: zExt.objectId().nullish(),
  automationExecutionId: zExt.objectId().nullish(),
  marketingMessageId: zExt.objectId().nullish(),
  category: createEnum(categories).nullish(),
  customerId: zExt.objectId(),
  channel: createEnum(MarketingChannel),
  channelId: z.string(),
  messageId: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  metrics: campaignRecipientMetricsSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
  status: createEnum(CampaignRecipientStatus),
  scheduledAt: z.date().nullish(),
  content: messageContentSchema.nullish(),
  cost: messageCostSchema.nullish(),
  sentAt: z.date().nullish(),
  attemptsLeft: z.number(),
  error: z.any().optional(),
});

export type CampaignRecipientMetrics = z.infer<
  typeof campaignRecipientMetricsSchema
>;

export const singleMetrics: (keyof CampaignRecipientMetrics)[] = [
  "sends",
  "deliveries",
  "bounces",
  "alreadyUnsubscribes",
  "invalidChannelIds",
  "suppressions",
  "smartSendingSkips",
  "unsupportedInternationals",
  "failures",
  "notEnabled",
  "quietHours",
];

export const multiMetrics: (keyof CampaignRecipientMetrics)[] = [
  "opens",
  "clicks",
  "complaints",
  "upsellRevenue",
  "upsellCount",
];

// Type assertion to ensure all metrics are covered
type AllMetrics =
  | (typeof singleMetrics)[number]
  | (typeof multiMetrics)[number];
type _assertAllMetricsCovered =
  keyof CampaignRecipientMetrics extends AllMetrics ? true : false;
const _assertAllMetricsCovered: _assertAllMetricsCovered = true;

export type CampaignRecipient = z.infer<typeof campaignRecipientSchema>;
